import { useMemo } from 'react';
import { CellProps, Column } from 'react-table';
import styled from 'styled-components';

import { OrderDTO, OrderStatus } from '../../../../api/api';
import Button from '../../../../components/button/Button';
import StatusTag from '../../../../components/status-tag/StatusTag';
import useTableInstance from '../../../../hooks/useTableInstance';
import { formatDateString, formatTimeAndDateString, sortDateTime } from '../../../../utils/dateHandling';
import { getEnumDisplayValue } from '../../../../utils/enumUtils';
import { getInitialsFromEmail } from '../../../../utils/initialsUtils';
import {
  DateRangeColumnFilter,
  SearchColumnFilter,
  SelectColumnFilter,
  dateRangeFilterFn,
  multiSelectFilterFn
} from '../../../../blocks/table/TableFilterUtils';

interface OrderData {
  status?: OrderStatus;
  transferId?: number;
  toWarehouseName?: number;
  create?: string;
  useWorkTaskOrdersTable?: string;
  description?: string;
  orderId?: number;
  workTaskId?: number;
}

interface SortBy {
  sortByColumn: string;
  desc: boolean;
}

const useOrderTable = (orders: OrderDTO[], openOrderReceipt: (order?: OrderDTO) => void, sortBy?: SortBy) => {
  const data = useMemo(() => {
    if (!orders.length) return [];

    return orders.map((order) => {
      return {
        status: order.orderStatus ? getEnumDisplayValue(order.orderStatus) : '-',
        transferId: order.transferId,
        toWarehouseName: order.toWarehouseName,
        created:
          formatTimeAndDateString(order.created ?? '') + ' (' + getInitialsFromEmail(order.createdByEmail ?? '') + ')',
        expectedReceiptDate:
          order.isFulfilled && order.expectedReceiptDate ? formatDateString(order.expectedReceiptDate ?? '') : 'Ukendt',
        description: order.description,
        orderId: order.orderId,
        workTaskId: order.workTaskId,
        showError: !order.isFulfilled
      } as OrderData;
    });
  }, [orders]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Status',
        accessor: 'status',
        width: 120,
        minWidth: 120,
        Filter: SelectColumnFilter,
        filter: multiSelectFilterFn,
        Cell: (cellProps: CellProps<OrderData>) => {
          const { cell } = cellProps;
          return <StatusTag orderStatusValue={cell.value}>{cell.value}</StatusTag>;
        }
      },
      {
        Header: 'Bestillingsnummer',
        accessor: 'transferId',
        width: 120,
        minWidth: 120,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: 'Bestilt til',
        accessor: 'toWarehouseName',
        width: 120,
        minWidth: 120,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: 'Forventet leveringsdato',
        accessor: 'expectedReceiptDate',
        width: 120,
        minWidth: 120,
        Filter: (_tableInstance, x) => DateRangeColumnFilter(_tableInstance, true),
        filter: dateRangeFilterFn,
        sortType: (a, b) => sortDateTime(a.values.expectedReceiptDate, b.values.expectedReceiptDate)
      },
      {
        Header: 'Bemærkning',
        accessor: 'description',
        width: 120,
        minWidth: 120,
        Filter: SearchColumnFilter,
        filter: 'text'
      },
      {
        Header: '',
        accessor: 'actions',
        width: 200,
        minWidth: 200,
        disableFilters: true,
        disableSortBy: true,
        Cell: ({ row }: CellProps<OrderData>) => {
          return (
            <>
              {row.original.transferId && (
                <ButtonsContainer>
                  <Button
                    variant="secondary"
                    onClick={() => openOrderReceipt(orders.find((order) => order.orderId === row.original.orderId))}
                  >
                    Se bestilling
                  </Button>
                </ButtonsContainer>
              )}
            </>
          );
        }
      }
    ] as Column<OrderData>[];
  }, [openOrderReceipt, orders]);

  return useTableInstance(data, columns, {
    initialState: {
      pageSize: 25,
      pageIndex: 0,
      sortBy: [
        {
          id: sortBy?.sortByColumn ?? 'created',
          desc: sortBy?.desc ?? true
        }
      ]
    }
  });
};

export default useOrderTable;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: right;
  justify-content: right;
  column-gap: ${(props) => props.theme.spacing(2)};
  margin-right: ${(props) => props.theme.spacing(5)};
`;
